import { showErrorToast } from '@bpinternal/ui-kit-next'
import { createContext, useContext, useState } from 'react'
import { useDiscoverUrls } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { KnowledgeSourceStepProps } from '~/features/knowledge-bases/types'
import { DiscoverPagesStep } from './DiscoverPagesStep'
import { SelectPagesStep } from './SelectPageStep'

export type SyncWebsiteStepState = 'discover' | 'select'

type SyncWebsiteContextValue = {
  syncWebsiteSubStep: SyncWebsiteStepState
  setSyncWebsiteSubStep: (subStep: SyncWebsiteStepState) => void
  currentUrl: string
  discoveredUrls: string[]
  discoverUrls: (url: string, includeGlobs: string[], excludeGlobs: string[]) => void
  discoverUrlsLoading: boolean
}

type SyncWebsiteContextProviderProps = {
  workspaceId: string
  botId: string
  kbId: string
  dsId?: string
  footerRef: React.RefObject<HTMLDivElement>
  onStepChange?: (step: SyncWebsiteStepState) => void
} & Omit<KnowledgeSourceStepProps, 'onFooterActionsChange'>

const SyncWebsiteContext = createContext<SyncWebsiteContextValue | null>(null)

export const SyncWebsiteContextProvider = ({ workspaceId, botId, ...props }: SyncWebsiteContextProviderProps) => {
  const [discoveredUrls, setDiscoveredUrls] = useState<string[]>([])
  const [currentUrl, setCurrentUrl] = useState('')
  const [syncWebsiteSubStep, setSyncWebsiteSubStep] = useState<SyncWebsiteStepState>('discover')

  const { mutate: _discoverUrls, isPending: discoverUrlsLoading } = useDiscoverUrls()

  const discoverUrls = (url: string, includeGlobs: string[], excludeGlobs: string[]) => {
    _discoverUrls(
      { workspaceId, botId, url, includeGlobs, excludeGlobs },
      {
        onSuccess: (urls: string[]) => {
          setDiscoveredUrls(urls)
          setCurrentUrl(url)
          setSyncWebsiteSubStep('select')
        },
        onError: () => {
          showErrorToast(`Failed to discover URL`)
        },
      }
    )
  }
  return (
    <SyncWebsiteContext.Provider
      value={{
        discoveredUrls,
        currentUrl,
        discoverUrls,
        syncWebsiteSubStep,
        setSyncWebsiteSubStep,
        discoverUrlsLoading,
      }}
    >
      {syncWebsiteSubStep === 'discover' ? (
        <DiscoverPagesStep workspaceId={workspaceId} botId={botId} {...props} />
      ) : (
        <SelectPagesStep workspaceId={workspaceId} botId={botId} {...props} />
      )}
    </SyncWebsiteContext.Provider>
  )
}

export const useSyncWebsiteContext = () => {
  const context = useContext(SyncWebsiteContext)
  if (!context) {
    throw new Error('useSyncWebsiteContext must be used within SyncWebsiteContextProvider')
  }
  return context
}
